import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import {
  aiEditEmailTemplate,
  AiEditEmailTurn,
} from '../services/emailApi';
import EmailPreview from './EmailPreview';

interface EmailAiEditorDialogProps {
  open: boolean;
  /** Read-only subject — shown in the preview but not edited by the AI. */
  subject: string;
  /** The HTML body currently saved in the form (before any pending change). */
  currentHtml: string;
  onClose: () => void;
  /** Called with the AI's HTML when the user clicks Apply. */
  onApply: (html: string) => void;
}

interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
  /**
   * For assistant turns: the proposed HTML for this revision. The latest
   * assistant turn's HTML is what gets applied when the user clicks Apply.
   */
  html?: string;
}

export default function EmailAiEditorDialog({
  open,
  subject,
  currentHtml,
  onClose,
  onApply,
}: EmailAiEditorDialogProps) {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Reset chat state whenever the dialog reopens with a fresh draft.
  useEffect(() => {
    if (!open) {
      setTurns([]);
      setPrompt('');
      setError(null);
      setLoading(false);
    }
  }, [open]);

  // The most recent assistant turn's HTML is what shows in the preview pane
  // and gets applied. When there are no assistant turns yet, fall back to
  // the current form HTML so the preview is never blank.
  const pendingHtml = useMemo(() => {
    for (let i = turns.length - 1; i >= 0; i -= 1) {
      const t = turns[i];
      if (t.role === 'assistant' && typeof t.html === 'string') return t.html;
    }
    return currentHtml;
  }, [turns, currentHtml]);

  const hasPendingChange = useMemo(() => {
    return turns.some((t) => t.role === 'assistant' && t.html && t.html !== currentHtml);
  }, [turns, currentHtml]);

  useEffect(() => {
    // Auto-scroll the chat column to the latest turn.
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns, loading]);

  const handleSend = async () => {
    const text = prompt.trim();
    if (!text || loading) return;
    setError(null);

    const nextTurns: ChatTurn[] = [...turns, { role: 'user', content: text }];
    setTurns(nextTurns);
    setPrompt('');
    setLoading(true);

    // History sent to the backend = everything BEFORE the current user prompt.
    // The route adds the current prompt + currentHtml itself, so we strip the
    // tail user turn here.
    const history: AiEditEmailTurn[] = turns.map((t) => ({
      role: t.role,
      content: t.content,
    }));

    try {
      const result = await aiEditEmailTemplate({
        prompt: text,
        currentHtml: pendingHtml,
        history,
      });
      setTurns((prev) => [
        ...prev,
        { role: 'assistant', content: result.message, html: result.html },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'AI editor request failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (!hasPendingChange) return;
    onApply(pendingHtml);
    onClose();
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AutoAwesomeIcon sx={{ color: '#7B1FA2' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Edit with AI
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              GPT reads your current HTML body and rewrites it from your prompt.
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} disabled={loading} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
            minHeight: { md: 520 },
          }}
        >
          {/* Left: chat column */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              borderRight: { md: '1px solid' },
              borderColor: { md: 'divider' },
              minWidth: 0,
            }}
          >
            <Box
              ref={scrollRef}
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                minHeight: 280,
                maxHeight: { md: 460 },
              }}
            >
              {turns.length === 0 && (
                <Box
                  sx={{
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    fontSize: 14,
                    py: 3,
                    textAlign: 'center',
                  }}
                >
                  Tell the AI what to change. Examples:
                  <br />
                  <em>"Make the call-to-action button green and say Register now."</em>
                  <br />
                  <em>"Add a section with three bullet points about the project."</em>
                </Box>
              )}
              {turns.map((turn, idx) => (
                <Box
                  key={`${turn.role}-${idx}`}
                  sx={{
                    alignSelf: turn.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '88%',
                    bgcolor: turn.role === 'user' ? '#1976D2' : 'action.hover',
                    color: turn.role === 'user' ? '#fff' : 'text.primary',
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      opacity: 0.8,
                      mb: 0.25,
                    }}
                  >
                    {turn.role === 'user' ? 'You' : 'AI'}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {turn.content}
                  </Typography>
                </Box>
              ))}
              {loading && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ alignSelf: 'flex-start', color: 'text.secondary' }}
                >
                  <CircularProgress size={14} />
                  <Typography variant="body2">AI is rewriting…</Typography>
                </Stack>
              )}
            </Box>

            {error && (
              <Alert severity="error" sx={{ m: 2, mt: 0 }}>
                {error}
              </Alert>
            )}

            <Box
              sx={{
                p: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                gap: 1,
              }}
            >
              <TextField
                placeholder="Describe the edit you want…"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void handleSend();
                  }
                }}
                multiline
                maxRows={4}
                fullWidth
                disabled={loading}
                size="small"
                inputProps={{ maxLength: 2000 }}
              />
              <Button
                onClick={handleSend}
                variant="contained"
                disabled={loading || prompt.trim().length === 0}
                startIcon={loading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SendIcon />}
                sx={{ textTransform: 'none', bgcolor: '#7B1FA2', '&:hover': { bgcolor: '#6A1B9A' } }}
              >
                Send
              </Button>
            </Box>
          </Box>

          {/* Right: live preview of pending HTML */}
          <Box sx={{ p: 2, bgcolor: 'background.default', minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                {hasPendingChange ? 'Proposed preview' : 'Current preview'}
              </Typography>
              {hasPendingChange && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#7B1FA2',
                    fontWeight: 700,
                    bgcolor: '#F3E5F5',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                  }}
                >
                  Unapplied AI changes
                </Typography>
              )}
            </Stack>
            <EmailPreview subject={subject} html={pendingHtml} />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button color="inherit" onClick={onClose} disabled={loading}>
          {hasPendingChange ? 'Discard' : 'Close'}
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={!hasPendingChange || loading}
          startIcon={<CheckIcon />}
          sx={{ textTransform: 'none', bgcolor: '#7B1FA2', '&:hover': { bgcolor: '#6A1B9A' } }}
        >
          Apply changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
